import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react';
import { kaiTypographyWeb } from '../../typography';
import { DropdownArrowIcon } from '../Icons';
import { adminCommonColors } from './adminCommonColors';
import { ADMIN_COMMON_ICON_SIZES } from './adminCommonTokens';
import { resolveStyleOverride, type StyleOverride } from './styleOverrides';

export const COMMON_NAV_TAB_COLORS = {
  text: adminCommonColors.text,
  textMuted: adminCommonColors.textMuted,
  textSoft: adminCommonColors.textTertiary,
} as const;

const COMMON_NAV_TAB_STYLE: CSSProperties = {
  ...kaiTypographyWeb.medium,
  color: COMMON_NAV_TAB_COLORS.textSoft,
  textDecoration: 'none',
  height: '100%',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  position: 'relative',
  transition: 'color 0.15s ease',
  border: 0,
  padding: 0,
  background: 'transparent',
  cursor: 'pointer',
  appearance: 'none',
};

export const COMMON_NAV_TAB_SELECTED_STYLE: CSSProperties = {
  color: COMMON_NAV_TAB_COLORS.text,
};

const COMMON_NAV_TAB_SELECTED_INDICATOR_STYLE: CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: -1,
  height: 2,
  borderRadius: 1,
  background: COMMON_NAV_TAB_COLORS.text,
  pointerEvents: 'none',
};

export const COMMON_NAV_TAB_TRAILING_STYLE: CSSProperties = {
  width: ADMIN_COMMON_ICON_SIZES.navTabChevron,
  height: ADMIN_COMMON_ICON_SIZES.navTabChevron,
  minWidth: ADMIN_COMMON_ICON_SIZES.navTabChevron,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  transform: 'translateY(1px)',
  transition: 'transform 0.15s ease',
};

export const COMMON_NAV_TAB_TRAILING_OPEN_STYLE: CSSProperties = {
  transform: 'translateY(1px) rotate(180deg)',
};

const DefaultChevronIcon = () => <DropdownArrowIcon size={ADMIN_COMMON_ICON_SIZES.navTabChevron} />;

export type CommonNavTabProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  label: ReactNode;
  selected?: boolean;
  trailing?: ReactNode;
  showChevron?: boolean;
  chevron?: ReactNode;
  trailingOpen?: boolean;
  styleOverride?: StyleOverride;
  selectedCss?: StyleOverride;
  trailingCss?: StyleOverride;
  trailingOpenCss?: StyleOverride;
};

// Web-only admin primitive extracted from renovator CommonNavTab without Emotion.
export const CommonNavTab = forwardRef<HTMLButtonElement, CommonNavTabProps>(
  (
    {
      label,
      selected = false,
      trailing,
      showChevron = false,
      chevron,
      trailingOpen = false,
      styleOverride: tabCss,
      selectedCss,
      trailingCss,
      trailingOpenCss,
      style,
      type = 'button',
      ...props
    },
    ref,
  ) => {
    const trailingContent = trailing ?? (showChevron ? (chevron ?? <DefaultChevronIcon />) : null);

    return (
      <button
        ref={ref}
        type={type}
        style={{
          ...COMMON_NAV_TAB_STYLE,
          ...style,
          ...resolveStyleOverride(tabCss),
          ...(selected ? COMMON_NAV_TAB_SELECTED_STYLE : undefined),
          ...(selected ? resolveStyleOverride(selectedCss) : undefined),
        }}
        {...props}
      >
        <span>{label}</span>
        {trailingContent && (
          <span
            data-kai-shiki-nav-tab-trailing='true'
            style={{
              ...COMMON_NAV_TAB_TRAILING_STYLE,
              ...resolveStyleOverride(trailingCss),
              ...(trailingOpen ? COMMON_NAV_TAB_TRAILING_OPEN_STYLE : undefined),
              ...(trailingOpen ? resolveStyleOverride(trailingOpenCss) : undefined),
            }}
          >
            {trailingContent}
          </span>
        )}
        {selected && (
          <span
            aria-hidden='true'
            data-kai-shiki-nav-tab-selected-indicator='true'
            style={COMMON_NAV_TAB_SELECTED_INDICATOR_STYLE}
          />
        )}
      </button>
    );
  },
);

CommonNavTab.displayName = 'CommonNavTab';
